import React, { useState } from 'react';

const PERIOD_OPTIONS = Array.from({ length: 7 }, (_, i) =>
  ['Q1', 'Q2', 'Q3', 'Q4'].map((q) => `${2020 + i}-${q}`)
).flat();

const SELECT_CLS =
  'w-full bg-white border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm text-gray-600 ' +
  'appearance-none cursor-pointer focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500';

const LABEL_CLS = 'block text-xs font-medium text-gray-700 mb-1';

function ChevronDown() {
  return (
    <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );
}

// Same "Common Name (CODE)" format as buildSpeciesOptions() in
// Admin/FoundationalTables/SpeciesLivestockRegistry/registryHelpers.js (the Breed
// Ecotype Registry's species dropdown), so species show identically everywhere.
function speciesLabel(s) {
  const code = s.species_code || s.speciesCode || s.code || '';
  const base = s.common_name_nigeria || code || 'Unnamed species';
  return `${base}${code ? ` (${code})` : ''}`;
}

function getSpeciesUuid(species) {
  return species?.uuid ?? species?.id ?? species?.species_id ?? species?.speciesUuid ?? '';
}

function getSpeciesCode(species) {
  return species?.species_code ?? species?.speciesCode ?? species?.code ?? '';
}

function FilterSelect({ label, value, onChange, children, placeholder = 'All' }) {
  return (
    <div>
      <label className={LABEL_CLS}>{label}</label>
      <div className="relative">
        <select value={value} onChange={onChange} className={SELECT_CLS}>
          <option value="">{placeholder}</option>
          {children}
        </select>
        <ChevronDown />
      </div>
    </div>
  );
}

const EMPTY = {
  state: '', species: '', productionSystem: '', countMethod: '',
  validationStatus: '', period: '', sex: '',
};

// sex ENUM — one record per sex (C1 long-format).
const SEX_FILTER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'combined', label: 'Combined' },
];

export default function PopulationFilterBar({ canCreate, onSearch, onFiltersChange, onAddRecord, refData = {}, hideFilters = false }) {
  const [searchInput, setSearchInput] = useState('');
  const [draft, setDraft] = useState(EMPTY);

  const {
    states = [],
    species = [],
    productionSystems = [],
    countMethods = [],
    validationStatuses = [],
  } = refData;

  const resolveSpeciesCode = (value) => {
    if (!value) return '';
    const match = species.find((s) => getSpeciesUuid(s) === value || getSpeciesCode(s) === value);
    return getSpeciesCode(match) || value;
  };

  // Applies a given draft immediately — filters take effect live as soon as
  // a dropdown changes, matching the rest of the app, instead of requiring
  // a separate "Apply" click.
  const applyFilters = (nextDraft) => {
    const active = Object.fromEntries(Object.entries(nextDraft).filter(([, v]) => v !== ''));
    active.species = resolveSpeciesCode(active.species);
    onFiltersChange(active);
  };

  const set = (key) => (e) => {
    const value = e.target.value;
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      applyFilters(next);
      return next;
    });
  };

  const hasActiveFilters = Object.values(draft).some((v) => v !== '') || searchInput !== '';

  const handleApply = () => applyFilters(draft);

  const handleReset = () => {
    setDraft(EMPTY);
    setSearchInput('');
    onFiltersChange({});
    onSearch('');
  };

  return (
    <div className="space-y-3 mb-4">

      {/* ── Row 1: Search + Create New ─────────────────────────────────── */}
      <div className="flex gap-3">
        <label className="flex-1 flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2.5 bg-white cursor-text focus-within:border-green-500 focus-within:ring-1 focus-within:ring-green-500">
          <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <circle cx={11} cy={11} r={8} /><path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            value={searchInput}
            onChange={(e) => { const val = e.target.value; setSearchInput(val); onSearch(val); }}
            placeholder="Search here"
            className="flex-1 text-sm text-gray-600 outline-none bg-transparent placeholder-gray-400"
          />
        </label>
        <button onClick={() => onSearch(searchInput)} className="px-5 py-2.5 bg-[#D9FBD4] border border-green-200 text-green-700 text-sm font-semibold rounded-lg hover:bg-green-100 transition-colors whitespace-nowrap">
          Search
        </button>
        {canCreate && (
          <button onClick={onAddRecord} className="flex items-center gap-2 px-5 py-2.5 bg-green-700 hover:bg-green-800 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap">
            <span className="text-base font-bold leading-none">+</span>
            Create New
          </button>
        )}
      </div>

      {/* ── Row 2: filter dropdowns ───────────────────────────────────── */}
      {!hideFilters && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <FilterSelect label="State" value={draft.state} onChange={set('state')}>
            {states.map((s) => <option key={s} value={s}>{s}</option>)}
          </FilterSelect>

          <FilterSelect label="Species" value={draft.species} onChange={set('species')}>
            {species.map((s) => (
              <option
                key={getSpeciesUuid(s) || getSpeciesCode(s)}
                value={getSpeciesUuid(s) || getSpeciesCode(s)}
              >
                {speciesLabel(s)}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect label="Production System" value={draft.productionSystem} onChange={set('productionSystem')}>
            {productionSystems.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </FilterSelect>

          <FilterSelect label="Count Method" value={draft.countMethod} onChange={set('countMethod')}>
            {countMethods.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </FilterSelect>

          <FilterSelect label="Sex" value={draft.sex} onChange={set('sex')}>
            {SEX_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </FilterSelect>

          <FilterSelect label="Validation Status" value={draft.validationStatus} onChange={set('validationStatus')}>
            {validationStatuses.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </FilterSelect>
        </div>
      )}

      {/* ── Row 3: Period + Reset + Apply ─────────────────────────────── */}
      {!hideFilters && (
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className={LABEL_CLS}>Set Period</label>
            <div className="relative">
              <select value={draft.period} onChange={set('period')} className={SELECT_CLS}>
                <option value="">All periods</option>
                {PERIOD_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <ChevronDown />
            </div>
          </div>
          {hasActiveFilters && (
            <button onClick={handleReset} className="px-4 py-2 bg-white border border-gray-200 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap">
              Reset
            </button>
          )}
          <button onClick={handleApply} className="px-6 py-2 bg-[#D9FBD4] border border-green-200 text-green-700 text-sm font-semibold rounded-lg hover:bg-green-100 transition-colors whitespace-nowrap">
            Apply
          </button>
        </div>
      )}

    </div>
  );
}
